import { useEffect, useMemo, useState } from 'react';

import { useOrganization } from '@clerk/clerk-react';

export interface OrganizationRole {
  isOrgLoaded: boolean;
  organizationId: string | null;
  organizationOwner: string | null;
  isOrgActive: boolean;
  isOrgAdmin: boolean;
  isOrgMember: boolean;
  hasOrgMembership: boolean;
  memberRole: string | null;
}

export const useOrganizationRole = (): OrganizationRole => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { isLoaded, organization, membership } = useOrganization();

  // Listen for session refresh events to force re-evaluation
  useEffect(() => {
    const handleSessionRefresh = () => {
      setRefreshTrigger((prev) => prev + 1);
    };

    window.addEventListener('org-session-refreshed', handleSessionRefresh);

    return () => {
      window.removeEventListener('org-session-refreshed', handleSessionRefresh);
    };
  }, []);

  return useMemo(() => {
    // Primary: Use Clerk organization hook
    if (organization && membership) {
      const role = membership.role;
      return {
        isOrgLoaded: isLoaded,
        organizationId: organization.id,
        organizationOwner: organization.publicMetadata?.owner as string | null,
        isOrgActive: organization.publicMetadata?.status === 'active',
        isOrgAdmin: role === 'org:admin',
        isOrgMember: role === 'org:member',
        hasOrgMembership: true,
        memberRole: role,
      };
    }

    // No organization membership
    return {
      isOrgLoaded: isLoaded,
      organizationId: null,
      organizationOwner: null,
      isOrgActive: false,
      isOrgAdmin: false,
      isOrgMember: false,
      hasOrgMembership: false,
      memberRole: null,
    };
  }, [organization, membership, refreshTrigger]);
};
